import { createSignal, Show } from "solid-js"
import { createSession } from "@/context/session-provider"
import { Box } from "@/designSystem/components/box"
import { Button } from "@/designSystem/components/button"
import { OtpInput } from "@/designSystem/components/otp-input"
import { Text } from "@/designSystem/components/text"
import { useI18nContext } from "@/i18n/i18n-solid"
import { betterAuthClient } from "@/lib/better-auth"
import { toast } from "@/lib/toast"
import { DashboardSection } from "./_dashboard-section"

export function EmailVerificationWidget() {
  const { LL } = useI18nContext()
  const { session, actions } = createSession()
  const [showOtp, setShowOtp] = createSignal(false)
  const [isSending, setIsSending] = createSignal(false)
  const [otp, setOtp] = createSignal("")
  const [isVerifying, setIsVerifying] = createSignal(false)
  const [errorMessage, setErrorMessage] = createSignal("")

  function email() {
    const s = session()
    return s.status === "logged-in" ? s.user.email : ""
  }

  async function handleSendOtp() {
    setIsSending(true)
    try {
      await betterAuthClient().emailOtp.sendVerificationOtp({
        email: email(),
        type: "sign-in",
      })
      setShowOtp(true)
    } catch {
      toast.error(LL().auth.failedToSend())
    } finally {
      setIsSending(false)
    }
  }

  async function handleVerifyOtp() {
    setIsVerifying(true)
    setErrorMessage("")
    try {
      const result = await betterAuthClient().signIn.emailOtp({
        email: email(),
        otp: otp(),
      })
      if (result?.error) {
        throw new Error(result.error.message)
      }
      toast.success(LL().auth.verifyEmailSuccess())
      await actions.refetchSession()
    } catch {
      setErrorMessage(LL().auth.invalidCode())
      setOtp("")
    } finally {
      setIsVerifying(false)
    }
  }

  function handleOtpChange(value: string) {
    setOtp(value)
    if (errorMessage()) setErrorMessage("")
  }

  return (
    <DashboardSection
      title={LL().dashboard.verifyEmail.title()}
      iconName="mail"
      hue="amber"
    >
      <Show
        when={showOtp()}
        fallback={
          <Box gap="16" fill>
            <Text fontSize="m" color="sand700">
              {LL().dashboard.verifyEmail.description()}
            </Text>
            <Box alignSelf="flex-end">
              <Button
                kind="solid"
                hue="amber"
                iconName="arrow-right"
                onClick={handleSendOtp}
                disabled={isSending()}
              >
                {isSending()
                  ? LL().auth.sending()
                  : LL().dashboard.verifyEmail.button()}
              </Button>
            </Box>
          </Box>
        }
      >
        <Box gap="16" fill flexDirection="column" alignItems="center">
          <Text fontSize="m" color="sand700">
            {LL().auth.enterCode()} <strong>{email()}</strong>
          </Text>
          <OtpInput
            value={otp()}
            onValueChange={handleOtpChange}
            onComplete={handleVerifyOtp}
            disabled={isVerifying()}
            error={!!errorMessage()}
          />
          <Show when={errorMessage()}>
            <Text fontSize="s" color="rose900">
              {errorMessage()}
            </Text>
          </Show>
          <Show when={isVerifying()}>
            <Text fontSize="m" color="sand600">
              {LL().auth.verifying()}
            </Text>
          </Show>
        </Box>
      </Show>
    </DashboardSection>
  )
}
